import { defineEventTaxonomy } from "@morganisbatman/observa-core";
import { z } from "zod";

// Client-side taxonomy for the `aucashapp` project (the Expo app). Mirrors the
// API's server-side taxonomy (apps/api/src/observa/taxonomy.ts): these are the
// app-emitted steps of the conversion funnels the backend can't see — a result
// screen being viewed, the affiliate hand-off to /compare, and account signup.
// The framework auto-events (observa.session.*, observa.page.viewed, app.opened)
// flow without being declared here. `appId` must match the registered slug.
//
// Use via useAppObserva() (src/observa/use-app-observa.ts) for a typed event().
const ProductSchema = z.enum(["AU08", "AU06", "AU13"]);

export const appEvents = defineEventTaxonomy({
  appId: "aucashapp",
  events: {
    // A persisted product's result screen finished loading and rendered. The
    // mid-funnel step between calculator.run (emitted server-side) and the
    // affiliate hand-off — the gap here is "ran it but never saw the answer".
    "result.viewed": z.object({ product: ProductSchema }),

    // The affiliate hand-off: the user tapped through to /compare from a result
    // screen. This is the revenue moment of the affiliate-led model (Compare
    // Club). `destination` is the URL opened so paid vs non-paid partners and
    // the originating product can be told apart. See docs/business-plan.md.
    "compare.clicked": z.object({
      product: ProductSchema,
      destination: z.string(),
    }),

    // An account was created in-app (signUp resolved to an authed session).
    // The retention anchor — pairs with cohorts once userId plumbing lands
    // (it now does: ObservaProvider.getUserId, src/auth.tsx).
    "account.signup": z.object({}),

    // A returning user signed in (password or magic-link). Distinguishes
    // returning engagement from first-touch signup in retention analysis.
    "account.login": z.object({ method: z.enum(["password", "magic-link"]) }),
  },
});
